import { ApiError } from '@shared/api/client';
import { resolveErrorCopy } from '@shared/errors/catalog';
import { formatPhoneForReading } from '@shared/format';
import { BanIcon, UnbanIcon } from '@shared/ui/icons';
import { StateBadge } from '@shared/ui/StateBadge';
import { useToast } from '@shared/ui/toast/ToastProvider';

import {
  AccountStateBadge,
  Avatar,
  DetailCard,
  DetailSection,
  MetaGrid,
  SectionAction,
} from '../_shared/components';
import { roleLabel, sharedCopy } from '../_shared/copy';
import { useBlockUser, useUnblockUser } from '../_shared/mutations';
import type { AdminUserDetail } from '../_shared/types';
import { copy } from './copy';
import { errors } from './errors';
import type { AskConfirm } from './types';

/**
 * §1 الملفّ — read, never written.
 *
 * **Every field here belongs to a row that places its editing elsewhere.** The
 * name, the handle, the e-mail and the contact number are the subscriber's own
 * settings; the avatar is uploaded from their profile; and changing the mobile
 * number is a **change of credential** that completes only against a code sent
 * to the new number. An edit control here would put an operator inside a
 * credential change, which is the one field the catalog protects by name. The
 * note under the card says that in the operator's terms rather than by citing a
 * row.
 *
 * The three operator verbs on this page — role, entitlement, block — are the
 * three the catalog does grant, and each sits on the head of its own section
 * rather than among the fields.
 *
 * **The display name is not repeated here.** The page head carries it as the
 * `<h1>`, and two first-level headings for one subject is the defect that rule
 * exists to prevent. What this panel carries is what the heading does not: the
 * identifier, the credential, the role, and the standing.
 */
export function ProfileSection({ user, askConfirm }: { user: AdminUserDetail; askConfirm: AskConfirm }) {
  const toast = useToast();
  const blockUser = useBlockUser();
  const unblockUser = useUnblockUser();

  function onWriteFailure(error: unknown) {
    const status = error instanceof ApiError ? error.status : 0;
    const code = error instanceof ApiError ? error.code : undefined;
    toast.show(resolveErrorCopy(code, status, errors).message, 'danger');
  }

  function askBlock() {
    askConfirm(
      {
        heading: sharedCopy.block.heading(user.display_name),
        body: sharedCopy.block.body,
        commitLabel: sharedCopy.block.commit,
        // Reversible-but-consequential: the account signs in again the moment
        // the block is lifted, and nothing it holds is destroyed.
        intent: 'warning',
      },
      async () => {
        await blockUser.mutateAsync(user.id);
        toast.show(sharedCopy.block.blocked, 'success');
      },
      onWriteFailure,
    );
  }

  function unblock() {
    // No dialog. A confirmation stands in front of what a press takes away;
    // restoring access takes nothing, and a dialog here would teach the operator
    // to dismiss dialogs — which is what makes the block dialog worthless.
    unblockUser.mutate(user.id, {
      onSuccess: () => toast.show(sharedCopy.block.unblocked, 'success'),
      onError: onWriteFailure,
    });
  }

  return (
    <DetailSection
      id="user-profile"
      heading={copy.profile.heading}
      action={
        user.is_blocked ? (
          <SectionAction onClick={unblock} icon={<UnbanIcon />}>
            {sharedCopy.block.unblockMenu}
          </SectionAction>
        ) : (
          <SectionAction onClick={askBlock} icon={<BanIcon />} tone="danger">
            {sharedCopy.block.menu}
          </SectionAction>
        )
      }
    >
      <DetailCard>
        <div className="p-4">
          <div className="grid gap-4 sm:gap-5 sm:[align-items:start] sm:[grid-template-columns:auto_minmax(0,1fr)]">
            <Avatar large />

            <MetaGrid>
              <dt>{copy.profile.handle}</dt>
              {/*
                The mono class sits on the **inner** element, never on the
                `<dd>`: it carries `direction: ltr`, and on the cell itself that
                flips the whole grid track so the value lands against the far
                edge instead of beside its own label.
              */}
              <dd>
                <bdi className="mono">@{user.handle}</bdi>
              </dd>

              <dt>{copy.profile.phone}</dt>
              <dd>
                {/*
                  The credential is stored and sent as issued — ASCII E.164 —
                  and read in the operator's own digits, national form, the way
                  the mockup draws it. `.mono` is for identifiers that must not
                  be transcribed; a number a person dials is not one.
                */}
                <bdi>{user.phone_e164 ? formatPhoneForReading(user.phone_e164) : '—'}</bdi>
              </dd>

              <dt>{copy.profile.role}</dt>
              <dd>
                {/*
                  A badge, not bare text — the pack draws one here, and the role
                  reads as a value of a closed vocabulary rather than as a word
                  in a sentence. Neutral on every member: a granted role is not a
                  state, and a colour for one would say that holding it is an
                  achievement the platform is congratulating.
                */}
                <StateBadge kind="neutral">{roleLabel(user.role)}</StateBadge>
              </dd>

              <dt>{copy.profile.state}</dt>
              <dd>
                <AccountStateBadge blocked={user.is_blocked} />
              </dd>
            </MetaGrid>
          </div>

          <p className="mb-0 mt-4 max-w-measure text-xs text-ink-muted">{copy.profile.note}</p>
        </div>
      </DetailCard>
    </DetailSection>
  );
}
